/**
 * ID Generation Utility
 * 
 * Generates cryptographically secure IDs matching the backend format.
 * Uses the same nanoid-based pattern as src/utils/index.ts on the server.
 */

/**
 * Generate a unique ID with optional prefix.
 * Uses crypto.randomUUID() for browser-native secure random generation,
 * formatted to match the backend's nanoid pattern.
 * 
 * @param prefix - Optional prefix (e.g., 'msg', 'client')
 * @returns ID in format "prefix_randomstring" or just "randomstring"
 * 
 * @example
 * generateId('msg')    // "msg_a1b2c3d4e5f6g7h8i9j0k"
 * generateId()         // "a1b2c3d4e5f6g7h8i9j0k"
 */
export function generateId(prefix?: string): string {
  // Use crypto.randomUUID() and take first 21 chars (excluding hyphens)
  // to match nanoid(21) format used on backend
  const uuid = crypto.randomUUID().replace(/-/g, '');
  const id = uuid.substring(0, 21);
  return prefix ? `${prefix}_${id}` : id;
}
